import React, { useCallback, useEffect, useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import Button from '../../../components/UI/Button';
import LoadingSpinner from '../../../components/UI/LoadingSpinner';
import SectionHeader from '../../../components/UI/SectionHeader';
import {
  phoneNumberHealthService,
  type PhoneHealthSummary,
  type PhoneHealthListItem,
  type AreaCodeGapRow,
  type ReplacementLogEntry,
  type HealthPeriod,
} from '../../../services/phoneNumberHealthService';
import HealthSummaryCards from './HealthSummaryCards';
import TimezoneInventory from './TimezoneInventory';
import PhoneNumberTable from './PhoneNumberTable';
import AreaCodeGapTable from './AreaCodeGapTable';
import ReplacementLog from './ReplacementLog';
import PhoneNumberDetailDrawer from './PhoneNumberDetailDrawer';
import HealthFilterBar from './HealthFilterBar';
import PurchaseNumberModal from './PurchaseNumberModal';

const apiError = (err: unknown, fallback: string) => {
  const e = err as { response?: { data?: { error?: { message?: string }; message?: string } }; message?: string };
  return e?.response?.data?.error?.message ?? e?.response?.data?.message ?? e?.message ?? fallback;
};

const PhoneHealthTab: React.FC = () => {
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [summary, setSummary] = useState<PhoneHealthSummary | null>(null);
  const [timezoneData, setTimezoneData] = useState<any>(null);
  const [items, setItems] = useState<PhoneHealthListItem[]>([]);
  const [gaps, setGaps] = useState<AreaCodeGapRow[]>([]);
  const [replacementLog, setReplacementLog] = useState<ReplacementLogEntry[]>([]);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [healthFilter, setHealthFilter] = useState('all');
  const [usageFilter, setUsageFilter] = useState('all');
  const [tzFilter, setTzFilter] = useState('all');
  const [period, setPeriod] = useState<HealthPeriod>('today');
  const [periodLabel, setPeriodLabel] = useState('Today');
  const [detailItem, setDetailItem] = useState<PhoneHealthListItem | null>(null);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [purchaseAreaCode, setPurchaseAreaCode] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const loadOverview = useCallback(async () => {
    try {
      const [sum, tz, gapRows, log, sug] = await Promise.all([
        phoneNumberHealthService.getSummary(period),
        phoneNumberHealthService.getByTimezone(),
        phoneNumberHealthService.getInventoryGaps(period),
        phoneNumberHealthService.getReplacementLog(),
        phoneNumberHealthService.getSuggestions(),
      ]);
      setSummary(sum);
      setPeriodLabel(sum.periodLabel ?? 'Today');
      setTimezoneData(tz);
      setGaps(gapRows);
      setReplacementLog(log);
      setSuggestions(sug);
      setLoadError(null);
    } catch (err) {
      setLoadError(apiError(err, 'Failed to load health data'));
    }
  }, [period]);

  const loadList = useCallback(async () => {
    setListLoading(true);
    try {
      const list = await phoneNumberHealthService.getList({
        page,
        limit: 25,
        search: search || undefined,
        healthStatus: healthFilter !== 'all' ? healthFilter : undefined,
        usageType: usageFilter !== 'all' ? usageFilter : undefined,
        timezone: tzFilter !== 'all' ? tzFilter : undefined,
        period,
      });
      setItems(list.items);
      setPeriodLabel(list.periodLabel ?? periodLabel);
      setPages(list.pagination.pages);
      setTotal(list.pagination.total);
    } catch (err) {
      toast.error(apiError(err, 'Failed to load numbers'));
    } finally {
      setListLoading(false);
    }
  }, [page, search, healthFilter, usageFilter, tzFilter, period]);

  const refreshAll = useCallback(async () => {
    setOverviewLoading(true);
    await loadOverview();
    await loadList();
    setOverviewLoading(false);
  }, [loadOverview, loadList]);

  useEffect(() => {
    (async () => {
      setOverviewLoading(true);
      await loadOverview();
      setOverviewLoading(false);
    })();
  }, [loadOverview]);

  useEffect(() => {
    if (!overviewLoading && !loadError) {
      loadList();
    }
  }, [loadList, overviewLoading, loadError]);

  const handleRecalculate = async () => {
    try {
      await phoneNumberHealthService.recalculate();
      toast.success('Health scores recalculated');
      await refreshAll();
    } catch (err) {
      toast.error(apiError(err, 'Recalculation failed'));
    }
  };

  const handleStatusChange = async (id: string, status: string) => {
    const prevItems = items;
    const prevSummary = summary;
    const prev = items.find((i) => i.id === id);
    if (!prev) return;

    setItems((cur) =>
      cur.map((n) =>
        n.id === id
          ? {
              ...n,
              healthStatus: status,
              healthStatusSince: new Date().toISOString(),
              healthStatusManual: true,
              healthManualCallsRemaining: 100,
            }
          : n
      )
    );
    if (summary && prev.healthStatus !== status) {
      setSummary({
        ...summary,
        healthy:
          summary.healthy +
          (status === 'healthy' ? 1 : 0) -
          (prev.healthStatus === 'healthy' ? 1 : 0),
        warning:
          summary.warning +
          (status === 'warning' ? 1 : 0) -
          (prev.healthStatus === 'warning' ? 1 : 0),
        replace:
          summary.replace +
          (status === 'replace' ? 1 : 0) -
          (prev.healthStatus === 'replace' ? 1 : 0),
        warmUp:
          summary.warmUp +
          (status === 'warm_up' ? 1 : 0) -
          (prev.healthStatus === 'warm_up' ? 1 : 0),
      });
    }
    if (detailItem?.id === id) {
      setDetailItem((d) =>
        d
          ? {
              ...d,
              healthStatus: status,
              healthStatusSince: new Date().toISOString(),
              healthStatusManual: true,
              healthManualCallsRemaining: 100,
            }
          : d
      );
    }

    try {
      await phoneNumberHealthService.setHealthStatus(id, status, 'Admin manual update');
      toast.success(`Status updated to ${status.replace('_', ' ')}`);
    } catch (err) {
      setItems(prevItems);
      if (prevSummary) setSummary(prevSummary);
      toast.error(apiError(err, 'Failed to update status'));
    }
  };

  const handleUnlock = async (id: string) => {
    try {
      await phoneNumberHealthService.unlockHealthStatus(id);
      setItems((cur) =>
        cur.map((n) =>
          n.id === id
            ? {
                ...n,
                healthStatusManual: false,
                healthManualCallsRemaining: undefined,
              }
            : n
        )
      );
      if (detailItem?.id === id) {
        setDetailItem((d) =>
          d
            ? {
                ...d,
                healthStatusManual: false,
                healthManualCallsRemaining: undefined,
              }
            : d
        );
      }
      toast.success('Manual lock cleared — auto health can recalculate');
    } catch (err) {
      toast.error(apiError(err, 'Failed to unlock status'));
    }
  };

  const handleApprove = async (suggestionId: string) => {
    try {
      await phoneNumberHealthService.approveReplacement(suggestionId);
      toast.success('Replacement approved');
      await refreshAll();
    } catch (err) {
      toast.error(apiError(err, 'Approval failed'));
    }
  };

  const handleReject = async (suggestionId: string) => {
    try {
      await phoneNumberHealthService.rejectReplacement(suggestionId);
      toast.success('Suggestion rejected');
      await refreshAll();
    } catch (err) {
      toast.error(apiError(err, 'Reject failed'));
    }
  };

  const openPurchaseForArea = (areaCode: string) => {
    setPurchaseAreaCode(areaCode);
    setPurchaseOpen(true);
  };

  if (overviewLoading && !summary && !loadError) {
    return (
      <div className="flex justify-center py-24">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (loadError && !summary) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <p className="text-sm text-muted-foreground">{loadError}</p>
        <Button variant="outlined" onClick={refreshAll}>
          <ArrowPathIcon className="h-4 w-4 mr-1" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SectionHeader
          title="Health Overview"
          description="Real-time metrics across your Telnyx number pool."
          className="mb-0"
        />
        <div className="flex gap-2">
          <Button variant="outlined" size="sm" onClick={handleRecalculate}>
            <ArrowPathIcon className="h-4 w-4 mr-1" />
            Recalculate
          </Button>
          <Button variant="outlined" size="sm" onClick={refreshAll} disabled={overviewLoading || listLoading}>
            <ArrowPathIcon className={`h-4 w-4 mr-1 ${overviewLoading || listLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <HealthSummaryCards summary={summary} loading={overviewLoading} />
      <TimezoneInventory data={timezoneData} gaps={gaps} periodLabel={periodLabel} onPurchaseArea={openPurchaseForArea} />

      <HealthFilterBar
        search={searchInput}
        onSearchChange={setSearchInput}
        healthFilter={healthFilter}
        onHealthFilterChange={(v) => { setHealthFilter(v); setPage(1); }}
        usageFilter={usageFilter}
        onUsageFilterChange={(v) => { setUsageFilter(v); setPage(1); }}
        tzFilter={tzFilter}
        onTzFilterChange={(v) => { setTzFilter(v); setPage(1); }}
        period={period}
        onPeriodChange={(v) => { setPeriod(v); setPage(1); }}
      />

      <PhoneNumberTable
        items={items}
        page={page}
        pages={pages}
        total={total}
        loading={listLoading}
        periodLabel={periodLabel}
        onPageChange={setPage}
        onViewDetails={setDetailItem}
        onStatusChange={handleStatusChange}
      />

      <AreaCodeGapTable rows={gaps} periodLabel={periodLabel} onPurchaseArea={openPurchaseForArea} />

      <ReplacementLog
        entries={replacementLog}
        suggestions={suggestions}
        onApprove={handleApprove}
        onReject={handleReject}
      />

      <PhoneNumberDetailDrawer
        item={detailItem}
        periodLabel={periodLabel}
        onClose={() => setDetailItem(null)}
        onStatusChange={handleStatusChange}
        onUnlock={handleUnlock}
      />

      <PurchaseNumberModal
        isOpen={purchaseOpen}
        onClose={() => { setPurchaseOpen(false); setPurchaseAreaCode(''); }}
        defaultAreaCode={purchaseAreaCode}
        onPurchased={refreshAll}
      />
    </div>
  );
};

export default PhoneHealthTab;
